import type {StandardSchemaV1} from '@standard-schema/spec';
import type {InferInput, InferOutput} from 'src/types';

/**
 * Validate the input against the provided schema.
 *
 * If the schema is undefined, the input is returned as is.
 * If validation fails, an error is logged and undefined is returned.
 * @param schema the schema to validate against
 * @param input the input to validate
 * @returns the validated output or undefined if validation fails
 */
export function validate<
  T extends StandardSchemaV1<I, O>,
  I = InferInput<T>,
  O = InferOutput<T>,
>(schema: T | undefined, input: InferOutput<T>): O | undefined {
  const result = schema?.['~standard'].validate(input);
  if (!result) {
    return;
  }

  if (result instanceof Promise) {
    // eslint-disable-next-line no-console
    console.error('validation error: Schema validation must be synchronous');
    return;
  }

  // if the `issues` field exists, the validation failed
  if (result.issues) {
    // eslint-disable-next-line no-console
    console.error(
      'validation error: Schema validation failed',
      JSON.stringify(result.issues, null, 2),
    );
    return;
  }

  return result.value;
}
